import type { ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { ROLE, type Role } from '@ovrsee/shared'
import { AppShell } from '../components/AppShell.js'
import { LoadingState } from '../components/LoadingState.js'
import { useAuth } from '../lib/auth.js'
import { RequireAuth } from './RequireAuth.js'
import { Login } from '../screens/auth/Login.js'
import { SignUp } from '../screens/auth/SignUp.js'
import { OAuthCallback } from '../screens/auth/OAuthCallback.js'
import { ChooseWorkspace } from '../screens/auth/ChooseWorkspace.js'
import { AcceptInvite } from '../screens/auth/AcceptInvite.js'
import { ForgotPassword } from '../screens/auth/ForgotPassword.js'
import { ResetPassword } from '../screens/auth/ResetPassword.js'
import { ManagerTasks } from '../screens/manager/Tasks.js'
import { MyDay } from '../screens/employee/MyDay.js'
import { ManagerOverview } from '../screens/manager/Overview.js'
import { ManagerAnalytics } from '../screens/manager/Analytics.js'
import { PersonAnalytics } from '../screens/manager/PersonAnalytics.js'
import { Team } from '../screens/manager/Team.js'
import { CommandCentre } from '../screens/super-manager/CommandCentre.js'
import { Innovation } from '../screens/innovation/Innovation.js'
import { Leave } from '../screens/leave/Leave.js'
import { MyProgress } from '../screens/employee/MyProgress.js'
import { Print } from '../screens/reports/Print.js'
import { Marketing } from '../screens/marketing/Marketing.js'
import { Admin } from '../screens/admin/Admin.js'

export function landingPathFor(role: Role): string {
  if (role === ROLE.SUPER_MANAGER) return '/command'
  if (role === ROLE.MANAGER) return '/overview'
  return '/my-day'
}

function Landing() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LoadingState label="Loading your workspace" />
  if (user === null) return <Marketing />
  return <Navigate to={landingPathFor(user.role)} replace />
}

function RoleRoute({ allow, children }: { allow: readonly Role[]; children: ReactNode }) {
  const { user } = useAuth()
  if (user === null) return <Navigate to="/login" replace />
  if (!allow.includes(user.role)) return <Navigate to={landingPathFor(user.role)} replace />
  return <>{children}</>
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/choose-workspace" element={<ChooseWorkspace />} />
      <Route path="/invite/accept" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route
        path="/tasks"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.MANAGER, ROLE.SUPER_MANAGER]}>
              <AppShell>
                <ManagerTasks />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/my-day"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.EMPLOYEE]}>
              <AppShell>
                <MyDay />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/my-progress"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.EMPLOYEE]}>
              <AppShell>
                <MyProgress />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/overview"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.MANAGER, ROLE.SUPER_MANAGER]}>
              <AppShell>
                <ManagerOverview />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/analytics"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.MANAGER, ROLE.SUPER_MANAGER]}>
              <AppShell>
                <ManagerAnalytics />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/analytics/person/:userId"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.MANAGER, ROLE.SUPER_MANAGER]}>
              <AppShell>
                <PersonAnalytics />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/team"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.MANAGER, ROLE.SUPER_MANAGER]}>
              <AppShell>
                <Team />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/command"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.SUPER_MANAGER]}>
              <AppShell>
                <CommandCentre />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
        />

      <Route
        path="/admin"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.SUPER_MANAGER]}>
              <AppShell>
                <Admin />
              </AppShell>
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/innovation"
        element={
          <RequireAuth>
            <AppShell>
              <Innovation />
            </AppShell>
          </RequireAuth>
        }
      />

      {/*
        Managerial like /analytics, which renders the same performance data. No
        AppShell: this route is printed, so it carries no sidebar or topbar.
      */}
      <Route
        path="/reports/print"
        element={
          <RequireAuth>
            <RoleRoute allow={[ROLE.MANAGER, ROLE.SUPER_MANAGER]}>
              <Print />
            </RoleRoute>
          </RequireAuth>
        }
      />

      <Route
        path="/leave"
        element={
          <RequireAuth>
            <AppShell>
              <Leave />
            </AppShell>
          </RequireAuth>
        }
      />

      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
